import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { STANDALONE_SLUGS } from "@/data/standaloneTopics";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SiteSettingsProvider } from "@/context/SiteSettingsContext";
import PublicLayout from "@/components/layout/PublicLayout";
import FloatingActions from "@/components/layout/FloatingActions";
import ScrollToTop from "@/components/ScrollToTop";
import ScrollProgress from "@/components/ScrollProgress";

const Home = lazy(() => import("@/pages/Home"));
const Jabalpur = lazy(() => import("@/pages/Jabalpur"));
const Nagpur = lazy(() => import("@/pages/Nagpur"));
const Courses = lazy(() => import("@/pages/Courses"));
const Boards = lazy(() => import("@/pages/Boards"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const About = lazy(() => import("@/pages/About"));
const PayNow = lazy(() => import("@/pages/PayNow"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const TutorRegistration = lazy(() => import("@/pages/TutorRegistration"));
const TutorJob = lazy(() => import("@/pages/TutorJob"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Policies = lazy(() => import("@/pages/Policies"));
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const BoardPage = lazy(() => import("@/pages/BoardPage"));
const GradePage = lazy(() => import("@/pages/GradePage"));
const Grades = lazy(() => import("@/pages/Grades"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminDemos = lazy(() => import("@/pages/admin/AdminDemos"));
const AdminTutors = lazy(() => import("@/pages/admin/AdminTutors"));
const AdminBlogs = lazy(() => import("@/pages/admin/AdminBlogs"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments"));
const AdminLogs = lazy(() => import("@/pages/admin/AdminLogs"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminPolicies = lazy(() => import("@/pages/admin/AdminPolicies"));
const BookMyTutorLanding = lazy(() => import("@/pages/BookMyTutorLanding"));
const AdminMasterData = lazy(() => import("@/pages/admin/AdminMasterData"));
const AdminEmailSettings = lazy(() => import("@/pages/admin/AdminEmailSettings"));
const AdminEmailTemplates = lazy(() => import("@/pages/admin/AdminEmailTemplates"));
const AdminEmailLogs = lazy(() => import("@/pages/admin/AdminEmailLogs"));
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam"));
const AdminRecycleBin = lazy(() => import("@/pages/admin/AdminRecycleBin"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const ParentDashboard = lazy(() => import("@/pages/dashboard/ParentDashboard"));
const TutorDashboard = lazy(() => import("@/pages/dashboard/TutorDashboard"));

// Programmatic SEO pages
const CityTopicPage = lazy(() => import("@/pages/seo/CityTopicPage"));
const LocalityPage = lazy(() => import("@/pages/seo/LocalityPage"));
const CountryPage = lazy(() => import("@/pages/seo/CountryPage"));
const OnlineTuitionHub = lazy(() => import("@/pages/seo/OnlineTuitionHub"));
const SitemapHtml = lazy(() => import("@/pages/seo/SitemapHtml"));
const TutorJobsHub = lazy(() => import("@/pages/seo/TutorJobsHub"));
const TutorJobPage = lazy(() => import("@/pages/seo/TutorJobPage"));
const PanIndiaTopicPage = lazy(() => import("@/pages/seo/PanIndiaTopicPage"));
const CountryCurriculumPage = lazy(() => import("@/pages/seo/CountryCurriculumPage"));

const Loader = () => (
  <div className="min-h-[60vh] grid place-items-center">
    <div className="w-10 h-10 border-2 border-brand-royal border-t-transparent rounded-full animate-spin" />
  </div>
);

const ProtectedRoute = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

const Public = ({ children }) => (
  <PublicLayout>
    <ScrollProgress />
    <Suspense fallback={<Loader />}>{children}</Suspense>
    <FloatingActions />
  </PublicLayout>
);

function App() {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Public><Home /></Public>} />
            <Route path="/jabalpur" element={<Public><Jabalpur /></Public>} />
            <Route path="/nagpur" element={<Public><Nagpur /></Public>} />
            <Route path="/courses" element={<Public><Courses /></Public>} />
            <Route path="/boards" element={<Public><Boards /></Public>} />
            <Route path="/how-it-works" element={<Public><HowItWorks /></Public>} />
            <Route path="/about" element={<Public><About /></Public>} />
            <Route path="/founder" element={<Public><About /></Public>} />
            <Route path="/team" element={<Public><About /></Public>} />
            <Route path="/pay-now" element={<Public><PayNow /></Public>} />
            <Route path="/book-demo" element={<Public><BookDemo /></Public>} />
            <Route path="/tutor-registration" element={<Public><TutorRegistration /></Public>} />
            <Route path="/become-a-tutor" element={<Public><TutorRegistration /></Public>} />
            <Route path="/tutor-job" element={<Public><TutorJob /></Public>} />
            <Route path="/career" element={<Public><TutorJob /></Public>} />
            <Route path="/careers" element={<Public><TutorJob /></Public>} />
            <Route path="/blog" element={<Public><Blog /></Public>} />
            <Route path="/blogs" element={<Public><Blog /></Public>} />
            <Route path="/blogs/:category" element={<Public><Blog /></Public>} />
            <Route path="/blog/:slug" element={<Public><BlogDetail /></Public>} />
            <Route path="/blogs/post/:slug" element={<Public><BlogDetail /></Public>} />
            <Route path="/contact" element={<Public><Contact /></Public>} />
            <Route path="/faq" element={<Public><FAQ /></Public>} />
            <Route path="/parent-faqs" element={<Public><FAQ /></Public>} />
            <Route path="/tutor-faqs" element={<Public><FAQ /></Public>} />
            <Route path="/policies/:slug" element={<Public><Policies /></Public>} />
            <Route path="/book-my-tutor" element={<Public><BookMyTutorLanding /></Public>} />
            <Route path="/app" element={<Public><BookMyTutorLanding /></Public>} />

            {/* Category landing pages */}
            <Route path="/home-tuition" element={<Public><CategoryPage /></Public>} />
            <Route path="/coaching-classes" element={<Public><CategoryPage /></Public>} />
            <Route path="/online-classes" element={<Public><CategoryPage /></Public>} />
            <Route path="/competitive-exams" element={<Public><CategoryPage /></Public>} />
            <Route path="/spoken-english" element={<Public><CategoryPage /></Public>} />
            <Route path="/computer-courses" element={<Public><CategoryPage /></Public>} />
            <Route path="/why-choose-us" element={<Public><CategoryPage /></Public>} />

            {/* Board landing pages */}
            <Route path="/cbse" element={<Public><BoardPage /></Public>} />
            <Route path="/icse" element={<Public><BoardPage /></Public>} />
            <Route path="/mp-board" element={<Public><BoardPage /></Public>} />
            <Route path="/maharashtra-board" element={<Public><BoardPage /></Public>} />
            <Route path="/ib" element={<Public><BoardPage /></Public>} />
            <Route path="/igcse" element={<Public><BoardPage /></Public>} />
            <Route path="/a-level" element={<Public><BoardPage /></Public>} />
            <Route path="/us-common-core" element={<Public><BoardPage /></Public>} />

            {/* Grade landing pages */}
            <Route path="/grades" element={<Public><Grades /></Public>} />
            <Route path="/nursery" element={<Public><GradePage /></Public>} />
            <Route path="/kg" element={<Public><GradePage /></Public>} />
            <Route path="/class-1" element={<Public><GradePage /></Public>} />
            <Route path="/class-2" element={<Public><GradePage /></Public>} />
            <Route path="/class-3" element={<Public><GradePage /></Public>} />
            <Route path="/class-4" element={<Public><GradePage /></Public>} />
            <Route path="/class-5" element={<Public><GradePage /></Public>} />
            <Route path="/class-6" element={<Public><GradePage /></Public>} />
            <Route path="/class-7" element={<Public><GradePage /></Public>} />
            <Route path="/class-8" element={<Public><GradePage /></Public>} />
            <Route path="/class-9" element={<Public><GradePage /></Public>} />
            <Route path="/class-10" element={<Public><GradePage /></Public>} />
            <Route path="/class-11" element={<Public><GradePage /></Public>} />
            <Route path="/class-12" element={<Public><GradePage /></Public>} />

            {/* Programmatic SEO routes — must come BEFORE auth/other catch-alls */}
            <Route path="/online-tuition" element={<Public><OnlineTuitionHub /></Public>} />
            <Route path="/online-tuition/:country" element={<Public><CountryPage /></Public>} />
            <Route path="/online-tuition/:country/:curriculum" element={<Public><CountryCurriculumPage /></Public>} />
            <Route path="/tutor-jobs" element={<Public><TutorJobsHub /></Public>} />
            <Route path="/tutor-jobs/:slug" element={<Public><TutorJobPage /></Public>} />
            <Route path="/jabalpur/tutor-jobs" element={<Public><TutorJobsHub /></Public>} />
            <Route path="/nagpur/tutor-jobs" element={<Public><TutorJobsHub /></Public>} />
            <Route path="/jabalpur/tutor-jobs/:slug" element={<Public><TutorJobPage /></Public>} />
            <Route path="/nagpur/tutor-jobs/:slug" element={<Public><TutorJobPage /></Public>} />
            <Route path="/sitemap" element={<Public><SitemapHtml /></Public>} />
            <Route path="/jabalpur/areas/:locality" element={<Public><LocalityPage /></Public>} />
            <Route path="/nagpur/areas/:locality" element={<Public><LocalityPage /></Public>} />
            <Route path="/jabalpur/:topic" element={<Public><CityTopicPage /></Public>} />
            <Route path="/nagpur/:topic" element={<Public><CityTopicPage /></Public>} />

            {/* Pan-India standalone SEO landing pages — one explicit Route per topic.
                STANDALONE_SLUGS = home-tutors-near-me, best-home-tutors, female-home-tutors,
                maths-tutors, cbse-tutors, neet-tutors, etc. */}
            {STANDALONE_SLUGS.map((slug) => (
              <Route
                key={slug}
                path={`/${slug}`}
                element={<Public><PanIndiaTopicPage /></Public>}
              />
            ))}

            <Route path="/login" element={<Public><Login /></Public>} />
            <Route path="/signup" element={<Public><Signup /></Public>} />
            <Route path="/admin-login" element={<AdminLogin />} />

            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="demos" element={<AdminDemos />} />
              <Route path="tutors" element={<AdminTutors />} />
              <Route path="blogs" element={<AdminBlogs />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="policies" element={<AdminPolicies />} />
              <Route path="master-data" element={<AdminMasterData />} />
              <Route path="email-settings" element={<AdminEmailSettings />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="email-logs" element={<AdminEmailLogs />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="recycle-bin" element={<AdminRecycleBin />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            <Route path="/parent/dashboard" element={<ProtectedRoute role="parent"><Public><ParentDashboard /></Public></ProtectedRoute>} />
            <Route path="/tutor/dashboard" element={<ProtectedRoute role="tutor"><Public><TutorDashboard /></Public></ProtectedRoute>} />

            <Route path="*" element={<Public><div className="max-w-3xl mx-auto px-4 py-32 text-center"><h1 className="font-display text-5xl">404</h1><p className="mt-4 text-slate-600">Page not found.</p></div></Public>} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </SiteSettingsProvider>
    </AuthProvider>
  );
}

export default App;
